/**
 * Unassigned Inbox — #1219 Block E
 * Eingehende Carrier-Mails ohne automatische Claim-Zuordnung.
 * @page /claims/inbox
 * @roles admin, manager, cs_teamlead
 * @i18n claims.unassigned.*
 * @endpoints GET /api/claims/inbound/unassigned, POST /api/claims/inbound/:id/assign,
 *            POST /api/claims/inbound/:id/discard
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { PageHeader, Button } from '../../components/ui';
import { Mail, Paperclip, Search, X, Trash2, ArrowRight, RefreshCw, FileText } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
    return data;
  });
}

function formatDate(iso) {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function UnassignedInbox() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [assignFor, setAssignFor] = useState(null); // inbound row
  const [claims, setClaims] = useState([]);
  const [claimSearch, setClaimSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/claims/inbound/unassigned');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Inbox fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
    const iv = setInterval(fetchRows, 60_000);
    return () => clearInterval(iv);
  }, [fetchRows]);

  // #1221 Bug 8+9: Server-Side-Suche statt Client-Filter — Backend matched per ILIKE
  useEffect(() => {
    if (!assignFor) return;
    const q = (claimSearch || '').trim();
    const url = q
      ? `/api/claims?limit=50&search=${encodeURIComponent(q)}`
      : '/api/claims?limit=50&sort=created_at&order=DESC';
    const ctrl = new AbortController();
    api(url, { signal: ctrl.signal }).then(data => {
      // #1225 Punkt 3 — /api/claims liefert { data: [...], total, page, limit } — vorher wurde data.data ignoriert
      const list = Array.isArray(data) ? data : (data?.data || data?.rows || data?.claims || []);
      setClaims(list);
    }).catch((err) => {
      if (err?.name !== 'AbortError') setClaims([]);
    });
    return () => ctrl.abort();
  }, [assignFor, claimSearch]);

  // #1225 Punkt 3 — Status-Badge-Map analog ClaimsDashboard (kompakt)
  const STATUS_BADGE = {
    submitted: { bg: 'bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', label: t('claims.status.submitted') || 'Eingereicht' },
    in_review: { bg: 'bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400', label: t('claims.status.in_review') || 'In Prüfung' },
    documents_missing: { bg: 'bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400', label: t('claims.status.documents_missing') || 'Dokumente fehlen' },
    approved: { bg: 'bg-green-500/10', text: 'text-green-700 dark:text-green-400', label: t('claims.status.approved') || 'Bestätigt' },
    at_carrier: { bg: 'bg-purple-500/10', text: 'text-purple-700 dark:text-purple-400', label: t('claims.status.at_carrier') || 'Beim Carrier' },
    waiting_for_carrier: { bg: 'bg-indigo-500/10', text: 'text-indigo-700 dark:text-indigo-400', label: t('claims.status.waiting_for_carrier') || 'Warte auf Carrier' },
    carrier_inquiry: { bg: 'bg-cyan-500/10', text: 'text-cyan-700 dark:text-cyan-400', label: t('claims.status.carrier_inquiry') || 'Carrier-Rückfrage' },
    carrier_response: { bg: 'bg-teal-500/10', text: 'text-teal-700 dark:text-teal-400', label: t('claims.status.carrier_response') || 'Carrier-Antwort' },
    objection: { bg: 'bg-red-500/10', text: 'text-red-700 dark:text-red-400', label: t('claims.status.objection') || 'Einspruch' },
    resolved: { bg: 'bg-green-600/10', text: 'text-green-800 dark:text-green-300', label: t('claims.status.resolved') || 'Abgeschlossen' },
    rejected: { bg: 'bg-gray-500/10', text: 'text-gray-700 dark:text-gray-400', label: t('claims.status.rejected') || 'Abgelehnt' },
  };

  const filtered = rows.filter(r => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (r.subject || '').toLowerCase().includes(s)
      || (r.from_email || '').toLowerCase().includes(s)
      || (r.body_text || '').toLowerCase().includes(s);
  });

  // Liste kommt jetzt schon vorgefiltert vom Server zurück
  const filteredClaims = claims;

  const handleAssign = async (claimId) => {
    if (!assignFor || !claimId) return;
    setBusy(true);
    try {
      await api(`/api/claims/inbound/${assignFor.id}/assign`, {
        method: 'POST',
        body: { claim_id: claimId },
      });
      setAssignFor(null);
      setClaimSearch('');
      await fetchRows();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async (inboundId) => {
    if (!confirm(t('claims.unassigned.discardConfirm') || 'Diese Mail verwerfen?')) return;
    try {
      await api(`/api/claims/inbound/${inboundId}/discard`, { method: 'POST' });
      await fetchRows();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title={t('claims.unassigned.title') || 'Unassigned Inbox'}
        subtitle={t('claims.unassigned.subtitle') || 'Eingehende Carrier-Mails ohne automatische Zuordnung'}
      />

      {/* Toolbar */}
      <div className="bg-surface-card border border-line-card rounded-card p-3 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-content-tertiary" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('claims.unassigned.searchPlaceholder') || 'Absender / Betreff / Inhalt…'}
            className="w-full pl-8 pr-3 py-1.5 text-[12px] bg-surface-card border border-line rounded-button text-content placeholder:text-content-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <span className="text-[11px] text-content-tertiary ml-auto">
          {filtered.length} / {rows.length} {t('claims.unassigned.entries') || 'Einträge'}
        </span>
        <button onClick={fetchRows} className="p-1.5 rounded hover:bg-surface-hover text-content-tertiary" title={t('common.refresh') || 'Aktualisieren'}>
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* List */}
      <div className="bg-surface-card border border-line-card rounded-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-content-tertiary text-[13px]">{t('common.loading') || 'Lade…'}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-content-tertiary text-[13px]">
            <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
            {t('claims.unassigned.empty') || 'Keine ungenehmigten Mails'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-inset border-b border-line">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-medium text-content-secondary uppercase">{t('claims.unassigned.from') || 'Von'}</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-medium text-content-secondary uppercase">{t('claims.unassigned.subject') || 'Betreff'}</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-medium text-content-secondary uppercase">{t('claims.unassigned.received') || 'Eingang'}</th>
                <th className="px-4 py-2.5 text-center text-[11px] font-medium text-content-secondary uppercase">{t('claims.unassigned.attachments') || 'Anhänge'}</th>
                <th className="px-4 py-2.5 text-right text-[11px] font-medium text-content-secondary uppercase">{t('claims.unassigned.actions') || 'Aktionen'}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <tr key={row.id} className="border-b border-line last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-3 text-[12px] text-content font-mono">{row.from_email || '–'}</td>
                  <td className="px-4 py-3 text-[12px] text-content max-w-md">
                    <div className="font-medium truncate" title={row.subject}>{row.subject || '(kein Betreff)'}</div>
                    {row.body_text && (
                      <div className="text-[11px] text-content-tertiary truncate mt-0.5" title={row.body_text}>
                        {row.body_text.slice(0, 120)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-content-secondary whitespace-nowrap">{formatDate(row.received_at)}</td>
                  <td className="px-4 py-3 text-center">
                    {row.attachment_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[12px] text-content-secondary">
                        <Paperclip className="w-3 h-3" /> {row.attachment_count}
                      </span>
                    ) : (
                      <span className="text-[12px] text-content-tertiary">–</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setAssignFor(row)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] bg-primary-500/10 text-primary-600 hover:bg-primary-500/20"
                      title={t('claims.unassigned.assign') || 'Claim zuordnen'}
                    >
                      <ArrowRight className="w-3 h-3" />
                      {t('claims.unassigned.assign') || 'Zuordnen'}
                    </button>
                    <button
                      onClick={() => handleDiscard(row.id)}
                      className="inline-flex items-center gap-1 ml-1 px-2.5 py-1 rounded text-[11px] bg-red-500/10 text-red-600 hover:bg-red-500/20"
                      title={t('claims.unassigned.discard') || 'Verwerfen'}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* #1225 Punkt 2 — Assign Modal: Backdrop bg-black/40 (statt /50) angeglichen an Bulk-Delete-Modal */}
      {assignFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && setAssignFor(null)}>
          <div className="bg-surface-card border border-line-card rounded-card p-6 w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-bold text-content">{t('claims.unassigned.assignTitle') || 'Mail zu Claim zuordnen'}</h3>
              <button onClick={() => setAssignFor(null)} className="text-content-tertiary hover:text-content">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[12px] text-content-secondary mb-3 bg-surface-inset rounded p-2">
              <div><span className="text-content-tertiary">Von:</span> {assignFor.from_email}</div>
              <div><span className="text-content-tertiary">Betreff:</span> {assignFor.subject || '–'}</div>
            </div>
            {/* #1225 Punkt 3 — Info-Box: Subject-Tag-Tip für automatische Zuordnung */}
            <div className="text-[11px] text-blue-700 dark:text-blue-300 mb-3 bg-blue-500/10 border border-blue-500/20 rounded p-2 leading-relaxed">
              {t('claims.unassigned.subjectTagHint') || 'Tipp: Wenn der Carrier in der Antwort den Subject-Tag [CLM-XXXX] beibehält, wird die Mail automatisch dem Claim zugeordnet — kein manuelles Zuordnen nötig.'}
            </div>
            <div className="relative mb-3">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-content-tertiary" />
              <input
                type="text"
                value={claimSearch}
                onChange={e => setClaimSearch(e.target.value)}
                placeholder={t('claims.unassigned.claimSearchPlaceholder') || 'Claim-Nummer / Tracking / Bestellung / Kunde…'}
                autoFocus
                className="w-full pl-8 pr-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content"
              />
            </div>
            <div className="flex-1 overflow-y-auto border border-line rounded-card">
              {filteredClaims.length === 0 ? (
                <div className="p-4 text-center text-content-tertiary text-[12px]">{t('claims.unassigned.noClaimsFound') || 'Keine Claims gefunden'}</div>
              ) : (
                filteredClaims.map(c => {
                  const badge = STATUS_BADGE[c.status] || { bg: 'bg-gray-500/10', text: 'text-gray-700 dark:text-gray-400', label: c.status || '–' };
                  return (
                    <button
                      key={c.id}
                      onClick={() => handleAssign(c.id)}
                      disabled={busy}
                      className="w-full text-left px-3 py-2 text-[12px] hover:bg-surface-hover border-b border-line last:border-0 flex items-center justify-between gap-3 disabled:opacity-50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-content">{c.claim_number || `#${c.id}`}</span>
                          <span className={`px-1.5 py-0.5 rounded-badge text-[10px] font-medium ${badge.bg} ${badge.text}`}>{badge.label}</span>
                        </div>
                        <div className="text-[11px] text-content-tertiary truncate mt-0.5">
                          {c.tracking_number ? `${c.tracking_number} • ` : ''}{c.customer_name || c.kunde_name || ''}
                        </div>
                      </div>
                      <ArrowRight className="w-3 h-3 text-content-tertiary shrink-0" />
                    </button>
                  );
                })
              )}
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setAssignFor(null)} disabled={busy}>
                {t('common.cancel') || 'Abbrechen'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
